import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '../ui/Card';
import { StatusPill } from '../ui/StatusPill';
import { CalendarIcon, ClockIcon, TagIcon, ChevronDownIcon, PackageIcon, ArrowRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Bid, getEffectiveStatus } from '../../data/bids';
interface BidCardProps {
  bid: Bid;
  index: number;
}
export function BidCard({
  bid,
  index
}: BidCardProps) {
  const [showLots, setShowLots] = useState(false);
  const [expandedLot, setExpandedLot] = useState<number | null>(null);
  const status = getEffectiveStatus(bid);
  const daysUntilDeadline = Math.ceil((new Date(bid.deadline).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    delay: index * 0.05
  }} data-id="element-27" data-mp="2009lf8xkhp4d">
      <Card hover data-id="element-28" data-mp="2im8l1g6tdjdv">
        <div className="flex items-start justify-between mb-3" data-id="element-29" data-mp="2ims635rrgnw4">
          <span className="text-sm font-mono text-gray-500" data-id="element-30" data-mp="2njikzprtxdfy">
            {bid.reference}
          </span>
          <StatusPill status={status} data-id="element-31" data-mp="2sj3ma6uh1xvu">
            {status === 'closing-soon' ? 'CLOSING SOON' : status === 'open' ? 'OPEN' : 'CLOSED'}
          </StatusPill>
        </div>

        <h3 className="text-xl font-bold text-charcoal mb-3" data-id="element-32" data-mp="328wv5038bywe">{bid.title}</h3>

        <p className="text-gray-600 mb-4" data-id="element-33" data-mp="369gqby48ssfw">{bid.description}</p>

        {bid.lots && bid.lots.length > 0 && <div className="mb-4" data-id="element-34" data-mp="3bae6k55ozcer">
            <button type="button" onClick={() => setShowLots(s => !s)} className="flex items-center gap-2 text-sm font-medium text-au-green hover:text-au-green-dark transition-colors" aria-expanded={showLots} data-id="element-35" data-mp="3g8xv8dbo72at">
              <PackageIcon size={16} data-id="element-36" data-mp="1lx0zi8673a9y" />
              <span data-id="element-37" data-mp="1dtp8k16t86ww">
                {showLots ? 'Hide' : 'View'} lot breakdown ({bid.lots.length}{' '}
                lots)
              </span>
              <ChevronDownIcon size={16} className={`transition-transform ${showLots ? 'rotate-180' : ''}`} data-id="element-38" data-mp="19uggd0rf4h3g" />
            </button>

            <AnimatePresence initial={false} data-id="element-39" data-mp="1isaemh8qtkt9">
              {showLots && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.25
          }} className="overflow-hidden" data-id="element-40" data-mp="1wsl69mhvn3fh">
                  <div className="mt-3 space-y-2 border-l-2 border-au-gold pl-4" data-id="element-41" data-mp="2bjagaljlqchr">
                    {bid.lots.map((lot, i) => {
                const isOpen = expandedLot === i;
                return <div key={i} className="border border-gray-border rounded-lg bg-white" data-id="element-42" data-mp="2z4l46z60aywu" data-mpi={i}>
                          <button type="button" onClick={() => setExpandedLot(isOpen ? null : i)} className="w-full flex items-start justify-between gap-3 p-3 text-left hover:bg-gray-50 transition-colors rounded-lg" aria-expanded={isOpen} data-id="element-43" data-mp="2q3mvw4ojc58g">
                            <div className="min-w-0" data-id="element-44" data-mp="27df0y9uzol3c">
                              <p className="font-semibold text-charcoal text-sm" data-id="element-45" data-mp="27c3zwhsxhrfx">
                                {lot.name}
                              </p>
                              <p className="text-xs text-gray-600 mt-0.5" data-id="element-46" data-mp="1xez9g5s1v9tb">
                                {lot.summary}
                              </p>
                            </div>
                            <ChevronDownIcon size={16} className={`text-gray-400 flex-shrink-0 mt-1 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-47" data-mp="2vrlcfyn7v98y" />
                          </button>

                          <AnimatePresence initial={false} data-id="element-48" data-mp="2h2p7iaggicck">
                            {isOpen && <motion.div initial={{
                      opacity: 0,
                      height: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto'
                    }} exit={{
                      opacity: 0,
                      height: 0
                    }} transition={{
                      duration: 0.2
                    }} className="overflow-hidden" data-id="element-49" data-mp="2d1lt7se24odg">
                                <div className="px-3 pb-3 pt-1 border-t border-gray-border" data-id="element-50" data-mp="inho5psydshb">
                                  <p className="text-xs font-semibold text-charcoal uppercase tracking-wide mb-2 mt-2" data-id="element-51" data-mp="nmb4dpzmjxl6">
                                    Indicative Products
                                  </p>
                                  <ul className="text-sm text-gray-700 space-y-1 mb-3" data-id="element-52" data-mp="3w01jypr9lviq">
                                    {lot.products.map((p, idx) => <li key={idx} className="flex items-start gap-2" data-id="element-53" data-mp="4vbhcubowxuj" data-mpi={idx}>
                                        <span className="text-au-green mt-0.5" data-id="element-54" data-mp="1g72ksyygf3ax">
                                          •
                                        </span>
                                        <span data-id="element-55" data-mp="37bw202rcx727">{p}</span>
                                      </li>)}
                                  </ul>
                                  <p className="text-xs font-semibold text-charcoal uppercase tracking-wide mb-1" data-id="element-56" data-mp="3haljvvj96rig">
                                    Use Cases
                                  </p>
                                  <p className="text-sm text-gray-600" data-id="element-57" data-mp="2omzhgqlrhsx6">
                                    {lot.useCases}
                                  </p>
                                </div>
                              </motion.div>}
                          </AnimatePresence>
                        </div>;
              })}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>}

        <div className="space-y-2 mb-4" data-id="element-58" data-mp="1vrhswhhd11wm">
          <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-59" data-mp="1vqgf458ym1b7">
            <CalendarIcon size={16} data-id="element-60" data-mp="20jt3qfdn283y" />
            <span data-id="element-61" data-mp="20kuj2w1otgrh">
              Published:{' '}
              {new Date(bid.publicationDate).toLocaleDateString('en-GB')}
            </span>
          </div>

          {status === 'closed' ? <div className="flex items-center gap-2 text-sm font-medium text-gray-500" data-id="element-62" data-mp="1qqu4m3kimz63">
              <ClockIcon size={16} data-id="element-63" data-mp="2ejan2f860t5x" />
              <span data-id="element-64" data-mp="2jhm00ytrbjsd">
                Closed on: {new Date(bid.deadline).toLocaleDateString('en-GB')}
              </span>
            </div> : <div className="flex items-center gap-2 text-sm font-medium text-au-red" data-id="element-65" data-mp="24jthdxswyf9y">
              <ClockIcon size={16} data-id="element-66" data-mp="29i4s0ar18tt8" />
              <span data-id="element-67" data-mp="29l94rwikcxvi">
                Deadline: {new Date(bid.deadline).toLocaleDateString('en-GB')}
              </span>
              {daysUntilDeadline > 0 && <span className="text-gray-500" data-id="element-68" data-mp="y52bffoxo492">
                  ({daysUntilDeadline} days remaining)
                </span>}
            </div>}

          <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-69" data-mp="ae3yp5nimzbg">
            <TagIcon size={16} data-id="element-70" data-mp="5fkfve47y3nb" />
            <span data-id="element-71" data-mp="5esko4eja7xk">{bid.category}</span>
          </div>
        </div>

        {bid.estimatedValue && <div className="pt-4 border-t border-gray-border" data-id="element-72" data-mp="1f0iqajnb016">
            <div className="flex items-center justify-between" data-id="element-73" data-mp="y4833f8j333i">
              <span className="text-sm text-gray-600" data-id="element-74" data-mp="y3g8o7qo3bet">Estimated Value</span>
              <span className="text-lg font-bold text-au-green" data-id="element-75" data-mp="t66debglk5j2">
                {bid.estimatedValue}
              </span>
            </div>
          </div>}

        {status === 'closed' && <div className="mt-4 pt-4 border-t border-gray-border" data-id="element-76" data-mp="kc1ovyoxr66p">
            <Link to="/procurement/contract-awards" className="inline-flex items-center gap-1 text-sm font-medium text-au-green hover:text-au-green-dark transition-colors" data-id="element-77" data-mp="1guh80wvp98yw">
              View award outcome
              <ArrowRightIcon size={14} data-id="element-78" data-mp="2usbqkjd9rgpr" />
            </Link>
          </div>}
      </Card>
    </motion.div>;
}